import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  LEASE_LABEL, leasePill, pill, LINK, MUTED, LABEL, TEXTAREA, GHOST, money, fmtYmd, fmtTs,
  BatteryBadges, makeModel, readJson, JSON_HEADERS, downloadBlob,
} from '../lib/equipment';
import { PosLeasePanel } from '../components/PosReports'; // @@POS_REPORTS_v104@@

// @@EQUIPMENT_LEASES_v87@@ HQ › Equipment › one lease.
// draft: edit everything + pick units -> Send. sent: waiting for the client
// (revert/cancel). active: check units back in. Accepted content is frozen.
const SHIP = [['name', 'Name', '1 1 220px'], ['company', 'Company', '1 1 220px'], ['street1', 'Street', '2 1 300px'],
  ['street2', 'Apt / suite', '1 1 160px'], ['city', 'City', '1 1 180px'], ['state', 'State', '0 1 90px'],
  ['postal', 'ZIP', '0 1 110px'], ['phone', 'Phone', '1 1 160px']];

function toForm(l) {
  return {
    title: l.title || '', event_name: l.event_name || '', start_date: l.start_date || '', end_date: l.end_date || '',
    ship_to: { ...l.ship_to }, rental_fee: l.rental_fee ?? '0.00', deposit: l.deposit ?? '0.00',
    processing_fee_pct: String(l.processing_fee_pct ?? ''), fee_schedule: l.fee_schedule || '',
    shipping_note: l.shipping_note || '', terms: l.terms || '', notes: l.notes || '',
  };
}

export default function EquipmentLease() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = !!user && ['admin', 'manager'].includes(user.role);
  const [lease, setLease] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [units, setUnits] = useState([]);
  const [pick, setPick] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [notes, setNotes] = useState('');

  function take(l) { setLease(l); setForm(toForm(l)); setNotes(l.notes || ''); setDirty(false); }
  async function load() {
    const r = await apiFetch('/api/equipment/leases/' + id);
    const d = await readJson(r);
    if (r.ok) take(d.lease); else setMsg(d.error || 'Could not load this lease.');
  }
  async function loadUnits() {
    const r = await apiFetch('/api/equipment/units');
    const d = await readJson(r);
    if (r.ok) setUnits((d.units || []).filter((u) => !u.lease && u.status === 'available'));
  }
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (lease && lease.status === 'draft' && canManage) loadUnits(); }, [lease && lease.status, lease && lease.items.length]);

  // POST/PUT/PATCH/DELETE helper: returns the body; applies the returned lease.
  async function call(method, path, body, label) {
    setBusy(label || method); setMsg('');
    const r = await apiFetch('/api/equipment/leases/' + id + path, {
      method, headers: body ? JSON_HEADERS : undefined, body: body ? JSON.stringify(body) : undefined,
    });
    const d = await readJson(r);
    setBusy('');
    if (!r.ok) { setMsg(d.error || 'Something went wrong.'); return null; }
    if (d.lease) take(d.lease);
    return d;
  }
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setDirty(true); };
  const setShip = (k) => (e) => { setForm({ ...form, ship_to: { ...form.ship_to, [k]: e.target.value } }); setDirty(true); };

  // @@LEASE_TERMS_v87_1@@ pull the current Standard terms into this draft (service fee, fee schedule, shipping note, terms).
  async function loadStandard() {
    if (!window.confirm('Replace this draft’s service fee, card fee schedule, shipping note and terms with the current standard terms?')) return;
    const r = await apiFetch('/api/equipment/settings');
    const d = await readJson(r);
    if (!r.ok) { setMsg(d.error || 'Could not load the standard terms.'); return; }
    const st = d.settings;
    setForm({ ...form, processing_fee_pct: String(st.default_processing_fee_pct), fee_schedule: st.fee_schedule_template, shipping_note: st.shipping_note, terms: st.terms_template });
    setDirty(true); setMsg('Standard terms loaded — Save draft to keep them.');
  }
  async function save() { const d = await call('PUT', '', form, 'save'); if (d) setMsg('Saved.'); return !!d; }
  async function send() {
    if (dirty && !(await save())) return;
    if (!window.confirm(`Send ${lease.number} to ${lease.client_name}? They’ll be able to review and accept it in their client portal.`)) return;
    const d = await call('POST', '/send', null, 'send');
    if (d) {
      setMsg(d.emailed ? `Sent. Emailed ${d.emailed} portal user${d.emailed === 1 ? '' : 's'}.`
        : d.lease.portal_users.length ? 'Sent. It’s waiting in their client portal, but no email went out — let them know.'
          : 'Sent, but nobody at this client has a portal login yet, so they can’t see it.');
    }
  }
  async function cancel() {
    if (!window.confirm(`Cancel ${lease.number}? Its units go back to available inventory.`)) return;
    await call('POST', '/cancel', {}, 'cancel');
  }
  async function del() {
    if (!window.confirm(`Delete ${lease.number}? This can’t be undone.`)) return;
    const r = await apiFetch('/api/equipment/leases/' + id, { method: 'DELETE' });
    const d = await readJson(r);
    if (r.ok) navigate('/equipment'); else setMsg(d.error || 'Could not delete.');
  }
  async function pdf() {
    const ok = await downloadBlob(apiFetch, `/api/equipment/leases/${id}/pdf`, `${lease.number}.pdf`);
    if (!ok) setMsg('Could not build the PDF.');
  }

  if (!lease || !form) return <div className="hq-page-content"><p className="hq-msg">{msg || 'Loading…'}</p></div>;
  const draft = lease.status === 'draft';
  const editable = draft && canManage;
  const a = lease.agreement;

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <p style={{ marginBottom: 8 }}><Link to="/equipment" style={LINK}>← Leases</Link></p>
        <h1 style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {lease.number} <span style={leasePill(lease.status)}>{LEASE_LABEL[lease.status]}</span>
          {lease.overdue && <span style={pill('red')}>Past end date</span>}
        </h1>
        <p>{lease.client_name} · {lease.title}{lease.event_name ? ' · ' + lease.event_name : ''}</p>
      </div>
      {msg && <p className="hq-msg" style={{ marginTop: -12, marginBottom: 16 }}>{msg}</p>}

      {lease.portal_users.length === 0 && ['draft', 'sent'].includes(lease.status) && (
        <div className="hq-panel" style={{ marginBottom: 16, borderColor: 'rgba(232,193,90,.4)' }}>
          <div style={{ padding: '14px 20px', color: '#e8c15a', fontSize: 14 }}>
            Nobody at {lease.client_name} has a client-portal login, so they can’t see or accept this lease yet.
            Link a contact to <Link to={'/clients/' + lease.client_id} style={LINK}>the client</Link> and invite them to the portal.
          </div>
        </div>
      )}

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div style={{ padding: '14px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {editable && <button className="hq-btn" onClick={save} disabled={!!busy || !dirty}>{busy === 'save' ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}</button>}
          {editable && <button className="hq-btn" style={{ background: '#31b44b', color: '#0a0a0b' }} onClick={send} disabled={!!busy}>Send to client</button>}
          {lease.status === 'sent' && <span style={{ fontSize: 14 }}>Sent {fmtTs(lease.sent_at)} — waiting for {lease.client_name} to accept{lease.portal_users.length ? ` (${lease.portal_users.join(', ')})` : ''}.</span>}
          {lease.status === 'sent' && canManage && <button className="hq-signout" style={GHOST} onClick={() => call('POST', '/revert', {}, 'revert')} disabled={!!busy}>Back to draft (edit)</button>}
          {['draft', 'sent'].includes(lease.status) && canManage && <button className="hq-signout" style={GHOST} onClick={cancel} disabled={!!busy}>Cancel lease</button>}
          {(draft || (lease.status === 'cancelled' && !lease.accepted_at)) && canManage && <button className="hq-signout" style={GHOST} onClick={del} disabled={!!busy}>Delete</button>}
          <button className="hq-signout" style={{ ...GHOST, marginLeft: 'auto' }} onClick={pdf}>Download PDF</button>
        </div>
      </div>

      {lease.accepted_at && (
        <div className="hq-panel" style={{ marginBottom: 20, borderColor: 'rgba(80,220,140,.3)' }}>
          <div className="hq-panel-head"><h2>Accepted</h2></div>
          <div style={{ padding: 20, fontSize: 14, display: 'grid', gap: 6 }}>
            <div>Signed by <strong>{lease.accepted_name}</strong> on {fmtTs(lease.accepted_at)} in the client portal{lease.accepted_ip ? ` (IP ${lease.accepted_ip})` : ''}.</div>
            <div style={MUTED}>The agreement below is the exact version they accepted. Fingerprint {lease.accepted_hash}</div>
          </div>
        </div>
      )}

      <ItemsPanel lease={lease} editable={editable} units={units} pick={pick} setPick={setPick} call={call} busy={busy} />

      {/* @@POS_REPORTS_v104@@ POS daily reports + credit notes + settlement (accepted leases only) */}
      {['active', 'returned'].includes(lease.status) && <PosLeasePanel leaseId={id} canManage={canManage} onChanged={load} />}

      {editable ? (
        <div className="hq-panel" style={{ marginBottom: 20 }}>
          <div className="hq-panel-head"><h2>Lease details</h2></div>
          <div style={{ padding: 20, display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ flex: '2 1 260px' }}><span style={LABEL}>Subject</span>
                <input className="hq-input" style={FULL} value={form.title} onChange={set('title')} maxLength={120} /></label>
              <label style={{ flex: '2 1 260px' }}><span style={LABEL}>Event / purpose</span>
                <input className="hq-input" style={FULL} value={form.event_name} onChange={set('event_name')} maxLength={160} placeholder="e.g. Fall tour merch table" /></label>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ flex: '1 1 160px' }}><span style={LABEL}>Rental start *</span>
                <input type="date" className="hq-input" style={FULL} value={form.start_date} onChange={set('start_date')} /></label>
              <label style={{ flex: '1 1 160px' }}><span style={LABEL}>Rental end *</span>
                <input type="date" className="hq-input" style={FULL} value={form.end_date} onChange={set('end_date')} /></label>
              <label style={{ flex: '1 1 130px' }}><span style={LABEL}>Hardware rental fee ($)</span>
                <input className="hq-input" style={FULL} inputMode="decimal" value={form.rental_fee} onChange={set('rental_fee')} /></label>
              <label style={{ flex: '1 1 130px' }}><span style={LABEL}>Deposit ($)</span>
                <input className="hq-input" style={FULL} inputMode="decimal" value={form.deposit} onChange={set('deposit')} /></label>
              <label style={{ flex: '1 1 130px' }}><span style={LABEL}>Event service fee (% of net sales)</span>
                <input className="hq-input" style={FULL} inputMode="decimal" value={form.processing_fee_pct} onChange={set('processing_fee_pct')} /></label>
            </div>
            <div>
              <span style={LABEL}>Ship to</span>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {SHIP.map(([k, label, flex]) => (
                  <input key={k} className="hq-input" style={{ flex, minWidth: 0 }} placeholder={label} aria-label={'Ship to ' + label}
                    value={form.ship_to[k] || ''} onChange={setShip(k)} maxLength={k.startsWith('street') ? 160 : 120} />
                ))}
              </div>
            </div>
            <label><span style={LABEL}>Shipping note</span>
              <input className="hq-input" style={FULL} value={form.shipping_note} onChange={set('shipping_note')} maxLength={500} /></label>
            <label><span style={LABEL}>Card processing fees (Square, passed through at cost)</span>
              <textarea style={{ ...TEXTAREA, minHeight: 170, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }} value={form.fee_schedule} onChange={set('fee_schedule')} /></label>
            <label><span style={LABEL}>Terms and conditions (this lease’s copy)</span>
              <textarea style={{ ...TEXTAREA, minHeight: 360 }} value={form.terms} onChange={set('terms')} />
              <span style={MUTED}><code>{'{service_fee_pct}'}</code>, <code>{'{return_address}'}</code> and <code>{'{tax_registrations}'}</code> are filled in automatically. The client sees the result.</span></label>
            <div><button type="button" className="hq-signout" style={GHOST} disabled={!!busy} onClick={loadStandard}>Replace with current standard terms</button></div>
            <label><span style={LABEL}>Internal notes (never shown to the client)</span>
              <textarea style={{ ...TEXTAREA, minHeight: 70 }} value={form.notes} onChange={set('notes')} /></label>
            <div><button className="hq-btn" onClick={save} disabled={!!busy || !dirty}>{dirty ? 'Save draft' : 'Saved'}</button></div>
          </div>
        </div>
      ) : (
        <>
          <AgreementView a={a} />
          <div className="hq-panel" style={{ marginBottom: 20 }}>
            <div className="hq-panel-head"><h2>Internal notes</h2></div>
            <div style={{ padding: 20, display: 'grid', gap: 10 }}>
              <textarea style={{ ...TEXTAREA, minHeight: 70 }} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManage} placeholder="Never shown to the client." />
              {canManage && <div><button className="hq-btn" disabled={!!busy || notes === (lease.notes || '')} onClick={() => call('PUT', '/notes', { notes }, 'notes')}>Save notes</button></div>}
            </div>
          </div>
        </>
      )}

      <div className="hq-panel">
        <div className="hq-panel-head"><h2>History</h2></div>
        <div style={{ padding: 20, display: 'grid', gap: 12 }}>
          <form style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} onSubmit={async (e) => { e.preventDefault(); if (await call('POST', '/note', { note }, 'note')) setNote(''); }}>
            <input className="hq-input" style={{ flex: '1 1 300px' }} placeholder="Add a note (shipped, tracking #, call log…)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
            <button className="hq-btn" type="submit" disabled={!note.trim() || !!busy}>Add note</button>
          </form>
          {lease.history.map((h) => (
            <div key={h.id} style={{ borderTop: '1px solid var(--hq-line)', paddingTop: 10, fontSize: 14 }}>
              <div style={MUTED}>{fmtTs(h.created_at)} · {h.via === 'portal' ? 'Client portal' : (h.by_name || 'HQ')}</div>
              {h.to_status && h.from_status !== h.to_status && <div>{h.from_status ? `${LEASE_LABEL[h.from_status] || h.from_status} → ` : ''}{LEASE_LABEL[h.to_status] || h.to_status}</div>}
              {h.note && <div style={{ whiteSpace: 'pre-wrap' }}>{h.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
const FULL = { width: '100%', boxSizing: 'border-box' };

function ItemsPanel({ lease, editable, units, pick, setPick, call, busy }) {
  const [ret, setRet] = useState({}); // itemId -> { condition, note }
  const total = lease.items.reduce((s, i) => s + Number(i.replacement_value || 0), 0);
  const active = lease.status === 'active';
  return (
    <div className="hq-panel" style={{ marginBottom: 20 }}>
      <div className="hq-panel-head"><h2>Equipment ({lease.items.length})</h2><span style={MUTED}>Total replacement value {money(total)}</span></div>
      <div style={{ overflowX: 'auto' }}>
        <table className="hq-table" style={{ minWidth: 760 }}>
          <thead><tr><th>Asset</th><th>Model / device ID</th><th>Batteries</th><th style={{ textAlign: 'right' }}>Replacement value</th><th>{active || lease.status === 'returned' ? 'Return' : ''}</th></tr></thead>
          <tbody>
            {lease.items.map((it) => {
              const r = ret[it.id] || { condition: 'good', note: '' };
              return (
                <tr key={it.id}>
                  <td><Link to={'/equipment/units/' + it.equipment_id} style={{ ...LINK, fontWeight: 600 }}>{it.asset_tag}</Link></td>
                  <td>{makeModel(it.make, it.model) || '—'}
                    <div style={{ ...MUTED, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{it.device_id || ''}{it.os_version ? ` · OS ${it.os_version}` : ''}</div></td>
                  <td><BatteryBadges u={it} /></td>
                  <td style={{ textAlign: 'right' }}>
                    {editable ? (
                      <input key={it.id + ':' + it.replacement_value} className="hq-input" style={{ width: 110, textAlign: 'right' }} inputMode="decimal" defaultValue={it.replacement_value ?? ''} aria-label={'Replacement value for ' + it.asset_tag}
                        onBlur={(e) => { if (e.target.value !== String(it.replacement_value ?? '')) call('PATCH', '/items/' + it.id, { replacement_value: e.target.value }, 'item'); }} />
                    ) : money(it.replacement_value)}
                  </td>
                  <td>
                    {editable && <button className="hq-signout" style={GHOST} onClick={() => call('DELETE', '/items/' + it.id, null, 'item')} disabled={!!busy}>Remove</button>}
                    {active && !it.released_at && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <select className="hq-select" value={r.condition} onChange={(e) => setRet({ ...ret, [it.id]: { ...r, condition: e.target.value } })} aria-label="Condition">
                          <option value="good">Back — good</option><option value="damaged">Back — damaged</option><option value="lost">Lost / not returned</option>
                        </select>
                        <input className="hq-input" style={{ width: 150 }} placeholder="Note" value={r.note} onChange={(e) => setRet({ ...ret, [it.id]: { ...r, note: e.target.value } })} />
                        <button className="hq-btn" disabled={!!busy} onClick={() => call('POST', `/items/${it.id}/return`, r, 'return')}>Check in</button>
                      </div>
                    )}
                    {it.released_at && it.return_condition && (
                      <span style={pill(it.return_condition === 'good' ? 'green' : it.return_condition === 'lost' ? 'red' : 'amber')}>
                        {it.return_condition === 'lost' ? 'Lost' : 'Back · ' + it.return_condition}</span>
                    )}
                    {it.released_at && it.return_condition && <div style={MUTED}>{fmtTs(it.returned_at || it.released_at)}{it.return_note ? ' — ' + it.return_note : ''}</div>}
                  </td>
                </tr>
              );
            })}
            {lease.items.length === 0 && <tr><td className="hq-empty" colSpan={5}>No units on this lease yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editable && (
        <div style={{ padding: '14px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', borderTop: '1px solid var(--hq-line)' }}>
          <select className="hq-select" style={{ flex: '1 1 300px' }} value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Add a unit">
            <option value="">{units.length ? 'Add an available unit…' : 'No available units — add one under Inventory'}</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.asset_tag} — {makeModel(u.make, u.model) || 'unit'}{u.device_id ? ` (${u.device_id})` : ''}</option>)}
          </select>
          <button className="hq-btn" disabled={!pick || !!busy} onClick={async () => { if (await call('POST', '/items', { equipment_id: pick }, 'item')) setPick(''); }}>Add unit</button>
        </div>
      )}
    </div>
  );
}

function AgreementView({ a }) {
  const ship = a.ship_to || {};
  const cityLine = [ship.city, [ship.state, ship.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return (
    <div className="hq-panel" style={{ marginBottom: 20 }}>
      <div className="hq-panel-head"><h2>Agreement (as the client sees it)</h2></div>
      <div style={{ padding: 20, display: 'grid', gap: 18, fontSize: 14 }}>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div><span style={LABEL}>Rental period</span>{fmtYmd(a.start_date)} – {fmtYmd(a.end_date)}</div>
          <div><span style={LABEL}>{a.service_fee_label ? 'Hardware rental fee' : 'Rental fee'}</span>{money(a.rental_fee)}</div>
          <div><span style={LABEL}>Deposit</span>{money(a.deposit)}</div>
          <div><span style={LABEL}>{a.service_fee_label || 'Processing fee'}</span>{a.processing_fee_pct}%{a.service_fee_label ? ' of net sales' : ''}</div>
          <div><span style={LABEL}>Ship to</span><div style={{ whiteSpace: 'pre-line' }}>{[ship.name, ship.company, ship.street1, ship.street2, cityLine, ship.phone].filter(Boolean).join('\n') || '—'}</div></div>
          <div><span style={LABEL}>Return to</span><div style={{ whiteSpace: 'pre-line' }}>{a.return_address}</div></div>
        </div>
        {a.shipping_note && <div style={MUTED}>{a.shipping_note}</div>}
        {(a.battery_marks || []).length > 0 && (
          <div><span style={LABEL}>Return package marking</span>{a.battery_marks.map((m) => <div key={m.code}><strong>{m.code}</strong> — {m.label}</div>)}
            <div style={MUTED}>FedEx Ground only. The PDF’s Return shipping page has the printable mark, checklist and address label. {/* @@RETURN_PAGE_v87_2@@ */}</div></div>
        )}
        {Array.isArray(a.tax_registrations) && (
          <div><span style={LABEL}>Sales tax — XYZ files for stops in</span>
            {a.tax_registrations.length ? a.tax_registrations.map((r) => (
              <div key={r.state}><strong>{r.state_name}</strong> — {r.tax_type}, permit <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{r.permit}</span>{r.agency ? ` · ${r.agency}` : ''}</div>
            )) : <div style={MUTED}>No registrations on file.</div>}
          </div>
        )}
        {a.fee_schedule && <div><span style={LABEL}>{a.card_fees_label || 'Processing fee schedule'}</span><pre style={PRE}>{a.fee_schedule}</pre></div>}
        <div><span style={LABEL}>Terms and conditions</span><pre style={{ ...PRE, fontFamily: 'inherit', fontSize: 14 }}>{a.terms}</pre></div>
      </div>
    </div>
  );
}
const PRE = { whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.55, background: 'var(--hq-field)', border: '1px solid var(--hq-line)', borderRadius: 10, padding: 14 };
